import { useState, useRef, useMemo } from 'react';
import { Check, Loader2, Copy, Lock, ChevronDown, X, Search, Gift, Shield, FlaskConical, UserCheck } from 'lucide-react';
import { useUtmParams } from '@/hooks/useUtmParams';
import { trackWaitlistSubmission } from '@/lib/analytics';
import { PRESET_TAGS, UNIQUE_TREATMENTS } from '@/data/treatments';
import { useTranslation } from 'react-i18next';
import { LuckyDrawModal } from '@/components/LuckyDrawModal';
import { LuckyDrawStatusCompact } from '@/components/LuckyDraw';
import { CountdownTimer } from '@/components/CountdownTimer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INTEREST_OPTIONS = [
  { key: 'waitlist.interest_longevity', value: 'Longevity & BioAge' },
  { key: 'waitlist.interest_aesthetic', value: 'Aesthetic Treatments' },
  { key: 'waitlist.interest_regenerative', value: 'Regenerative Therapies' },
  { key: 'waitlist.interest_all', value: 'Not sure — advise me' },
];

const REFERRAL_SOURCES = [
  { key: 'waitlist.source_instagram', value: 'Instagram' },
  { key: 'waitlist.source_facebook', value: 'Facebook' },
  { key: 'waitlist.source_google', value: 'Google Search' },
  { key: 'waitlist.source_friend', value: 'Friend / Referral' },
  { key: 'waitlist.source_tiktok', value: 'TikTok' },
  { key: 'waitlist.source_xiaohongshu', value: 'Xiaohongshu' },
  { key: 'waitlist.source_dcmall', value: 'Advertising Board @ DC MALL' },
  { key: 'waitlist.source_other', value: 'Other' },
];

const AGE_RANGES = [
  { key: 'waitlist.age_under_25', value: '<25' },
  { key: 'waitlist.age_25_34', value: '25-34' },
  { key: 'waitlist.age_35_44', value: '35-44' },
  { key: 'waitlist.age_45_54', value: '45-54' },
  { key: 'waitlist.age_55_plus', value: '55+' },
];

/* Auto-detect country from browser locale */
const detectCountryCode = () => {
  try {
    const lang = navigator.language || navigator.userLanguage || '';
    const region = lang.split('-')[1]?.toUpperCase();
    const map = { MY: '+60', SG: '+65', ID: '+62', TH: '+66', PH: '+63', VN: '+84', BN: '+673', IN: '+91', CN: '+86', JP: '+81', KR: '+82', HK: '+852', TW: '+886', AE: '+971', SA: '+966', GB: '+44', US: '+1', AU: '+61' };

    return map[region] || '+60';
  } catch { return '+60'; }
};

const treatmentTags = PRESET_TAGS;

const STEP_LABELS = ['waitlist.step_info', 'waitlist.step_contact', 'waitlist.step_interests'];

const countryCodes = [
  { code: '+60', label: 'MY', name: 'Malaysia' },
  { code: '+65', label: 'SG', name: 'Singapore' },
  { code: '+62', label: 'ID', name: 'Indonesia' },
  { code: '+66', label: 'TH', name: 'Thailand' },
  { code: '+63', label: 'PH', name: 'Philippines' },
  { code: '+84', label: 'VN', name: 'Vietnam' },
  { code: '+673', label: 'BN', name: 'Brunei' },
  { code: '+91', label: 'IN', name: 'India' },
  { code: '+86', label: 'CN', name: 'China' },
  { code: '+81', label: 'JP', name: 'Japan' },
  { code: '+82', label: 'KR', name: 'South Korea' },
  { code: '+852', label: 'HK', name: 'Hong Kong' },
  { code: '+886', label: 'TW', name: 'Taiwan' },
  { code: '+971', label: 'AE', name: 'UAE' },
  { code: '+966', label: 'SA', name: 'Saudi Arabia' },
  { code: '+44', label: 'UK', name: 'United Kingdom' },
  { code: '+1', label: 'US', name: 'United States' },
  { code: '+61', label: 'AU', name: 'Australia' },
  { code: 'custom', label: 'Other', name: 'other' },
];

export const WaitlistSection = () => {
  const { t } = useTranslation();
  const bullets = [
    t('waitlist.bullet1'),
    t('waitlist.bullet2'),
    t('waitlist.bullet3'),
    t('waitlist.bullet4'),
  ];
  const utmParams = useUtmParams();
  const formStartTime = useRef(Date.now());
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    interest: '',
    age_range: '',
    referral_source: '',
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [countryCode, setCountryCode] = useState('+60');
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [position, setPosition] = useState(null);
  const [luckyTicket, setLuckyTicket] = useState(null);
  const [submittedPhone, setSubmittedPhone] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [slideDir, setSlideDir] = useState('right');
  const [consent, setConsent] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef(null);

  const MAX_TAGS = 3;
  const [customTagInput, setCustomTagInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const customInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const toggleTag = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) return prev.filter((t) => t !== tag);
      if (prev.length >= MAX_TAGS) return prev; // enforce limit
      return [...prev, tag];
    });
  };

  // Suggestions: search full curated catalogue, exclude already-selected
  const suggestions = useMemo(() => {
    const input = customTagInput.trim().toLowerCase();
    if (!input) return [];
    return UNIQUE_TREATMENTS.filter(
      (tag) =>
        !selectedTags.includes(tag) &&
        tag.toLowerCase().includes(input)
    ).slice(0, 8); // cap at 8 suggestions for performance
  }, [customTagInput, selectedTags]);

  const addFromSuggestion = (tag) => {
    if (!tag || selectedTags.length >= MAX_TAGS) return;
    if (selectedTags.includes(tag)) return;
    setSelectedTags((prev) => [...prev, tag]);
    setCustomTagInput('');
    setShowSuggestions(false);
  };

  const removeTag = (tag) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleCustomInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Only allow selecting from suggestions — no free text
      if (suggestions.length > 0) {
        addFromSuggestion(suggestions[0]);
      }
    }
  };

  const validateStep1 = () => {
    const errs = {};
    if (!formData.full_name || formData.full_name.trim().length < 2) {
      errs.full_name = 'Full name is required (min 2 characters)';
    }
    const emailTrimmed = formData.email ? formData.email.trim() : '';
    const emailPattern = /^[a-zA-Z0-9]([a-zA-Z0-9._%+\-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.\-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
    if (!emailTrimmed || !emailPattern.test(emailTrimmed) || /\.\./.test(emailTrimmed)) {
      errs.email = 'Valid email address required';
    }
    if (!formData.age_range) {
      errs.age_range = t('waitlist.age_required');
    }
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    const phoneDigits = formData.phone.replace(/[\s\-()+ ]/g, '');
    if (!formData.phone || phoneDigits.length < 7) {
      errs.phone = 'Valid phone number required (min 7 digits)';
    }
    if (!formData.interest) {
      errs.interest = 'Please select your primary interest';
    }
    if (!formData.referral_source) {
      errs.referral_source = t('waitlist.referral_required');
    }
    return errs;
  };

  const handleNextStep1 = (e) => {
    e.preventDefault();
    const errs = validateStep1();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setApiError('');
    setSlideDir('right');
    setStep(2);
  };

  const handleNextStep2 = (e) => {
    e.preventDefault();
    const errs = validateStep2();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setApiError('');
    setSlideDir('right');
    setStep(3);
  };

  const handleBack = () => {
    setSlideDir('left');
    setStep((s) => Math.max(1, s - 1));
    setErrors({});
    setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    // Consent validation
    if (!consent) {
      setErrors({ consent: t('waitlist.consent_required') });
      setIsSubmitting(false);
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    // Spam: honeypot
    if (honeypot) {
      setTimeout(() => { setIsSubmitting(false); setIsSuccess(true); setPosition(999); }, 1200);
      return;
    }
    // Spam: too fast
    const elapsed = Date.now() - formStartTime.current;
    if (elapsed < 3000) {
      setTimeout(() => { setIsSubmitting(false); setIsSuccess(true); setPosition(999); }, 1200);
      return;
    }

    try {
      let fullPhone = formData.phone.trim();
      if (countryCode === 'custom') {
        if (!fullPhone.startsWith('+')) fullPhone = '+' + fullPhone;
      } else if (!fullPhone.startsWith('+')) {
        fullPhone = countryCode + fullPhone;
      }

      const payload = {
        full_name: formData.full_name,
        phone: fullPhone,
        email: formData.email,
        interest: formData.interest,
        wished_treatments: selectedTags,
        custom_wish: null,
        source: 'landing_prelaunch',
        age_range: formData.age_range || null,
        referral_source: formData.referral_source || null,
        consent,
        ...utmParams,
      };

      const res = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.ok) {
        trackWaitlistSubmission(formData);
        setPosition(data.position || null);
        setLuckyTicket(data.lucky_draw_ticket || null);
        setSubmittedPhone(fullPhone);
        setIsSuccess(true);
        if (data.lucky_draw_ticket) {
          setTimeout(() => setShowModal(true), 500);
        }
      } else if (data.detail) {
        const msg = Array.isArray(data.detail) ? data.detail.map((d) => d.msg).join('. ') : data.detail;
        setApiError(msg);
      } else {
        setApiError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (apiError) setApiError('');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = window.location.href;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const inputStyle = (field) => ({
    backgroundColor: '#ffffff',
    color: 'var(--vc-ink)',
    borderColor: errors[field] ? 'var(--vc-danger)' : '#e2e0db',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
  });

  const focusHandler = (field) => ({
    onFocus: (e) => {
      if (!errors[field]) e.currentTarget.style.borderColor = 'var(--vc-gold)';
      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(214,168,74,0.25)';
    },
    onBlur: (e) => {
      if (!errors[field]) e.currentTarget.style.borderColor = '#e2e0db';
      e.currentTarget.style.boxShadow = 'none';
    },
  });

  const selectedCountry = countryCodes.find((c) => c.code === countryCode) || countryCodes[0];
  const isCustomCode = countryCode === 'custom';

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="py-16 lg:py-24 px-6 sm:px-8"
      style={{ backgroundColor: 'var(--vc-bg-2)' }}
    >
      <div className="max-w-[1160px] mx-auto">
        {/* Countdown + Trust Badges */}
        <div className="flex flex-col items-center mb-12 lg:mb-16">
          <CountdownTimer />
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(214,168,74,0.1)',
                  border: '1px solid rgba(214,168,74,0.2)',
                }}
              >
                <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: 'var(--vc-gold)' }} />
              </div>
              <span
                className="text-[11px] sm:text-xs tracking-wide"
                style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('hero.badge_private')}
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(214,168,74,0.1)',
                  border: '1px solid rgba(214,168,74,0.2)',
                }}
              >
                <FlaskConical className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: 'var(--vc-gold)' }} />
              </div>
              <span
                className="text-[11px] sm:text-xs tracking-wide"
                style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('hero.badge_evidence')}
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(214,168,74,0.1)',
                  border: '1px solid rgba(214,168,74,0.2)',
                }}
              >
                <UserCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: 'var(--vc-gold)' }} />
              </div>
              <span
                className="text-[11px] sm:text-xs tracking-wide"
                style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('hero.badge_physician')}
              </span>
            </div>
          </div>
        </div>

        {/* Centered title + lucky draw badge */}
        <div className="text-center mb-10 lg:mb-14">
          <h2
            className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-[34px]"
            style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-fg)' }}
          >
            {t('waitlist.title')}
          </h2>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{
            backgroundColor: 'rgba(214,168,74,0.08)', border: '1px solid rgba(214,168,74,0.2)',
          }}>
            <Gift className="w-3.5 h-3.5" style={{ color: 'var(--vc-gold)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
              {t('lucky_draw.every_signup')}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* LEFT: Form Card (primary action, shows first on mobile) */}
          <div className="lg:col-span-7 order-1">
            <div
              className="rounded-[24px] relative"
              style={{ backgroundColor: 'var(--vc-card-light)', boxShadow: 'var(--vc-shadow-form)', minHeight: '420px' }}
            >
              {/* Form Steps */}
              <div
                className="p-5 sm:p-8 lg:p-10"
                style={{
                  opacity: isSuccess ? 0 : 1,
                  transform: isSuccess ? 'scale(0.96)' : 'scale(1)',
                  transition: 'opacity 300ms ease, transform 300ms ease',
                  position: isSuccess ? 'absolute' : 'relative',
                  inset: 0,
                  pointerEvents: isSuccess ? 'none' : 'auto',
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-ink)' }}>
                    {t('form_header.title')}
                  </h3>
                </div>
                {/* Step labels */}
                <div className="mt-3 mb-5 flex items-center gap-1 sm:gap-1">
                  {STEP_LABELS.map((label, i) => {
                    const s = i + 1;
                    const isActive = step === s;
                    const isDone = step > s;
                    return (
                      <div key={s} className="flex items-center gap-1">
                        {i > 0 && <div className="w-3 sm:w-6 h-px mx-0.5" style={{ backgroundColor: isDone ? 'var(--vc-gold)' : '#e2e0db' }} />}
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0"
                            style={{
                              backgroundColor: isDone ? 'var(--vc-gold)' : isActive ? 'var(--vc-gold)' : '#e2e0db',
                              color: isDone || isActive ? '#fff' : '#999',
                            }}>
                            {isDone ? '✓' : s}
                          </div>
                          <span className="text-[10px] sm:text-xs font-medium"
                            style={{ color: isActive ? 'var(--vc-ink)' : 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                            {t(label)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {apiError && (
                  <div data-testid="waitlist-error-message" className="mt-4 p-3 rounded-xl text-sm" style={{ backgroundColor: 'rgba(239,68,68,0.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)', fontFamily: 'Inter, sans-serif' }}>
                    {apiError}
                  </div>
                )}

                {/* Honeypot */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                  <label htmlFor="vc-website">Website</label>
                  <input id="vc-website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                </div>

                {/* Step container with slide animation */}
                <div className="overflow-hidden">
                  <div
                    key={step}
                    style={{
                      animation: `${slideDir === 'right' ? 'slideInRight' : 'slideInLeft'} 0.3s ease-out forwards`,
                    }}
                  >

                {/* ─── Step 1: Name + Email ─── */}
                {step === 1 && (
                  <form onSubmit={handleNextStep1} className="mt-6 space-y-5">
                    <div>
                      <label htmlFor="waitlist-name" className="block text-sm font-medium mb-2" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.label_name')}</label>
                      <input id="waitlist-name" data-testid="waitlist-full-name-input" type="text" value={formData.full_name} onChange={(e) => handleChange('full_name', e.target.value)} placeholder={t('waitlist.placeholder_name')} aria-label={t('waitlist.label_name')} className="w-full h-12 px-4 rounded-xl border outline-none transition-colors duration-200" style={inputStyle('full_name')} {...focusHandler('full_name')} />
                      {errors.full_name && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.full_name}</p>}
                    </div>
                    <div>
                      <label htmlFor="waitlist-email" className="block text-sm font-medium mb-2" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.label_email')}</label>
                      <input id="waitlist-email" data-testid="waitlist-email-input" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder={t('waitlist.placeholder_email')} aria-label={t('waitlist.label_email')} className="w-full h-12 px-4 rounded-xl border outline-none transition-colors duration-200" style={inputStyle('email')} {...focusHandler('email')} />
                      {errors.email && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.email}</p>}
                    </div>
                    {/* Age Range (optional) */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                        {t('waitlist.label_age')}
                      </label>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {AGE_RANGES.map((ar) => (
                          <button
                            key={ar.value}
                            type="button"
                            onClick={() => handleChange('age_range', formData.age_range === ar.value ? '' : ar.value)}
                            className="h-8 sm:h-9 px-3 sm:px-4 rounded-full text-xs sm:text-sm font-medium border transition-colors duration-200"
                            style={{
                              backgroundColor: formData.age_range === ar.value ? 'var(--vc-gold)' : '#ffffff',
                              color: formData.age_range === ar.value ? '#fff' : 'var(--vc-ink)',
                              borderColor: formData.age_range === ar.value ? 'var(--vc-gold)' : errors.age_range ? 'var(--vc-danger)' : '#e2e0db',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {t(ar.key)}
                          </button>
                        ))}
                      </div>
                      {errors.age_range && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.age_range}</p>}
                    </div>
                    <button data-testid="waitlist-next-button" type="submit" className="w-full h-12 rounded-xl font-medium transition-colors duration-200 active:scale-[0.98] text-sm" style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold-2)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold)'}>
                      {t('waitlist.next')}
                    </button>
                  </form>
                )}

                {/* ─── Step 2: Phone + Interest ─── */}
                {step === 2 && (
                  <form onSubmit={handleNextStep2} className="mt-6 space-y-5">
                    <div>
                      <label htmlFor="waitlist-phone" className="block text-sm font-medium mb-2" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.label_phone')}</label>
                      <p className="text-[11px] sm:text-xs mb-2.5 flex items-center gap-1.5" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
                        <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        {t('lucky_draw.whatsapp_notice')}
                      </p>
                      <div className="flex items-stretch">
                        <div className="relative" ref={dropdownRef}>
                          <button type="button" data-testid="country-code-selector" onClick={() => setShowCodeDropdown(!showCodeDropdown)} className="flex items-center gap-1 h-12 px-2 sm:px-3 rounded-l-xl border border-r-0 transition-colors duration-200" style={{ backgroundColor: '#f7f6f3', borderColor: errors.phone ? 'var(--vc-danger)' : '#e2e0db', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500, minWidth: isCustomCode ? '60px' : '72px' }}>
                            {isCustomCode ? <span>{t('waitlist.country_other')}</span> : <><span>{selectedCountry.label}</span><span>{selectedCountry.code}</span></>}
                            <ChevronDown className="w-3 h-3 ml-0.5" style={{ color: '#9ca3af' }} />
                          </button>
                          {showCodeDropdown && (
                            <div className="absolute top-full left-0 mt-1 rounded-xl border overflow-hidden z-50" style={{ backgroundColor: '#ffffff', borderColor: '#e2e0db', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', width: 'min(220px, calc(100vw - 48px))', maxHeight: '240px', overflowY: 'auto' }}>
                              {countryCodes.map((cc) => (
                                <button key={cc.code} type="button" onClick={() => { setCountryCode(cc.code); setShowCodeDropdown(false); if (cc.code === 'custom') { setFormData((prev) => ({ ...prev, phone: '+' })); } }} className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: cc.code === countryCode ? 'var(--vc-gold)' : 'var(--vc-ink)', backgroundColor: cc.code === countryCode ? 'rgba(214,168,74,0.08)' : 'transparent' }} onMouseEnter={(e) => { if (cc.code !== countryCode) e.currentTarget.style.backgroundColor = '#f7f6f3'; }} onMouseLeave={(e) => { if (cc.code !== countryCode) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                                  <span className="font-medium w-8">{cc.label}</span>
                                  <span className="text-xs" style={{ color: '#9ca3af' }}>{cc.name}</span>
                                  <span className="ml-auto font-medium">{cc.code === 'custom' ? '' : cc.code}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <input id="waitlist-phone" data-testid="waitlist-phone-input" type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder={isCustomCode ? '+XX XXX XXX XXXX' : '12 345 6789'} aria-label="Phone" className="w-full h-12 px-4 rounded-r-xl border outline-none transition-colors duration-200" style={inputStyle('phone')} {...focusHandler('phone')} />
                      </div>
                      {errors.phone && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.phone}</p>}
                    </div>
                    <div>
                      <label htmlFor="waitlist-interest" className="block text-sm font-medium mb-2" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.label_interest')}</label>
                      <Select value={formData.interest} onValueChange={(val) => handleChange('interest', val)}>
                        <SelectTrigger data-testid="waitlist-interest-select" className="w-full h-12 px-4 rounded-xl border" style={{ backgroundColor: '#ffffff', color: formData.interest ? 'var(--vc-ink)' : '#9ca3af', borderColor: errors.interest ? 'var(--vc-danger)' : '#e2e0db', fontFamily: 'Inter, sans-serif', fontSize: '16px' }} aria-label={t('waitlist.label_interest')}>
                          <SelectValue placeholder={t('waitlist.select_interest')} />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border" style={{ backgroundColor: '#ffffff', borderColor: '#e2e0db', fontFamily: 'Inter, sans-serif' }}>
                          {INTEREST_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-sm cursor-pointer" style={{ color: 'var(--vc-ink)' }}>{t(opt.key)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.interest && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.interest}</p>}
                    </div>
                    {/* How did you hear about us? (optional) */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                        {t('waitlist.label_referral')}
                      </label>
                      <Select value={formData.referral_source} onValueChange={(val) => handleChange('referral_source', val)}>
                        <SelectTrigger className="w-full h-12 px-4 rounded-xl border" style={{ backgroundColor: '#ffffff', color: formData.referral_source ? 'var(--vc-ink)' : '#9ca3af', borderColor: errors.referral_source ? 'var(--vc-danger)' : '#e2e0db', fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                          <SelectValue placeholder={t('waitlist.select_referral')} />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border" style={{ backgroundColor: '#ffffff', borderColor: '#e2e0db', fontFamily: 'Inter, sans-serif' }}>
                          {REFERRAL_SOURCES.map((rs) => (
                            <SelectItem key={rs.value} value={rs.value} className="text-sm cursor-pointer" style={{ color: 'var(--vc-ink)' }}>{t(rs.key)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.referral_source && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.referral_source}</p>}
                    </div>
                    <div className="flex gap-2 sm:gap-3">
                      <button type="button" onClick={handleBack} className="h-12 px-4 sm:px-5 rounded-xl font-medium border transition-colors duration-200 active:scale-[0.98] text-sm" style={{ borderColor: '#e2e0db', color: 'var(--vc-ink-muted)', backgroundColor: 'transparent', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.back')}</button>
                      <button data-testid="waitlist-step2-next" type="submit" className="flex-1 h-12 rounded-xl font-medium transition-colors duration-200 active:scale-[0.98] text-sm" style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold-2)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold)'}>
                        {t('waitlist.next')}
                      </button>
                    </div>
                  </form>
                )}

                {/* ─── Step 3: Treatment Tags + Custom Tag Input ─── */}
                {step === 3 && (
                  <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                        {t('waitlist.treatment_title')}
                      </label>
                      <p className="text-xs mb-3" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>
                        {t('waitlist.treatment_hint')}
                      </p>

                      {/* Selected tags display */}
                      {selectedTags.length > 0 && (
                        <div data-testid="selected-tags" className="flex flex-wrap gap-2 mb-3">
                          {selectedTags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium border transition-all duration-200"
                              style={{
                                backgroundColor: 'rgba(214,168,74,0.12)',
                                borderColor: 'var(--vc-gold)',
                                color: 'var(--vc-gold)',
                                fontFamily: 'Inter, sans-serif',
                              }}
                            >
                              <Check className="w-3.5 h-3.5" />
                              {tag}
                              <button
                                type="button"
                                data-testid={`remove-tag-${tag.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                                onClick={() => removeTag(tag)}
                                className="ml-0.5 rounded-full p-0.5 transition-colors duration-150"
                                style={{ color: 'var(--vc-gold)' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Preset treatment tags */}
                      <div data-testid="treatment-tags" className="flex flex-wrap gap-2">
                        {treatmentTags
                          .filter((tag) => !selectedTags.includes(tag))
                          .map((tag) => {
                            const atLimit = selectedTags.length >= MAX_TAGS;
                            return (
                              <button
                                key={tag}
                                type="button"
                                data-testid={`tag-${tag.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                                onClick={() => { if (!atLimit) toggleTag(tag); }}
                                disabled={atLimit}
                                aria-disabled={atLimit}
                                className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border transition-all duration-200 active:scale-[0.96]"
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderColor: '#e2e0db',
                                  color: atLimit ? '#c5c2ba' : 'var(--vc-ink-muted)',
                                  fontFamily: 'Inter, sans-serif',
                                  opacity: atLimit ? 0.5 : 1,
                                  cursor: atLimit ? 'not-allowed' : 'pointer',
                                  pointerEvents: atLimit ? 'none' : 'auto',
                                }}
                              >
                                {tag}
                              </button>
                            );
                          })}
                      </div>

                      {/* Limit indicator */}
                      <p className="mt-2.5 text-xs" style={{ color: selectedTags.length >= MAX_TAGS ? 'var(--vc-gold)' : '#9ca3af', fontFamily: 'Inter, sans-serif' }}>
                        {selectedTags.length} / {MAX_TAGS} {t('waitlist.treatment_counter')}
                      </p>
                    </div>

                    {/* Search from curated catalogue — no free text */}
                    {selectedTags.length < MAX_TAGS && (
                      <div className="relative">
                        <label htmlFor="custom-tag-input" className="block text-sm font-medium mb-2" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                          {t('waitlist.treatment_search_label')} <span className="font-normal" style={{ color: '#9ca3af' }}>{t('waitlist.treatment_search_sub')}</span>
                        </label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9ca3af' }} />
                          <input
                            ref={customInputRef}
                            id="custom-tag-input"
                            data-testid="custom-tag-input"
                            type="text"
                            value={customTagInput}
                            onChange={(e) => {
                              setCustomTagInput(e.target.value);
                              setShowSuggestions(true);
                            }}
                            onKeyDown={handleCustomInputKeyDown}
                            onFocus={() => { if (customTagInput.trim()) setShowSuggestions(true); }}
                            onBlur={() => {
                              setTimeout(() => setShowSuggestions(false), 200);
                            }}
                            placeholder={t('waitlist.treatment_search')}
                            autoComplete="off"
                            className="w-full h-11 pl-10 pr-4 rounded-xl border outline-none transition-colors duration-200"
                            style={{
                              backgroundColor: '#ffffff',
                              color: 'var(--vc-ink)',
                              borderColor: '#e2e0db',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '14px',
                            }}
                          />
                        </div>

                        {/* Autocomplete suggestions dropdown */}
                        {showSuggestions && customTagInput.trim() && (
                          <div
                            ref={suggestionsRef}
                            data-testid="tag-suggestions"
                            className="absolute top-full left-0 right-0 mt-1 rounded-xl border overflow-hidden z-50"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#e2e0db',
                              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                              maxHeight: '200px',
                              overflowY: 'auto',
                            }}
                          >
                            {suggestions.length > 0 ? (
                              suggestions.map((tag) => (
                                <button
                                  key={tag}
                                  type="button"
                                  data-testid={`suggestion-${tag.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    addFromSuggestion(tag);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors duration-100"
                                  style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-ink)' }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.06)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--vc-gold)' }} />
                                  {tag}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>
                                {t('waitlist.treatment_no_match')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-center" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                      {t('waitlist.limited')}
                    </p>

                    {/* PDPA Consent */}
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="mt-0.5">
                        <input
                          type="checkbox"
                          data-testid="consent-checkbox"
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                          className="sr-only"
                        />
                        <div
                          className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors duration-200"
                          style={{
                            borderColor: consent ? 'var(--vc-gold)' : errors.consent ? 'var(--vc-danger)' : '#d1d0cd',
                            backgroundColor: consent ? 'var(--vc-gold)' : 'transparent',
                          }}
                        >
                          {consent && <Check className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>
                      <span className="text-xs leading-relaxed" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                        {t('waitlist.consent_text')}
                      </span>
                    </label>
                    {errors.consent && <p className="text-xs -mt-2" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.consent}</p>}

                    <div className="flex gap-2 sm:gap-3">
                      <button type="button" onClick={handleBack} className="h-12 px-4 sm:px-5 rounded-xl font-medium border transition-colors duration-200 active:scale-[0.98] text-sm" style={{ borderColor: '#e2e0db', color: 'var(--vc-ink-muted)', backgroundColor: 'transparent', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.back')}</button>
                      <button data-testid="waitlist-submit-button" type="submit" disabled={isSubmitting} className="flex-1 h-12 rounded-xl font-medium transition-colors duration-200 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 text-sm" style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }} onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--vc-gold-2)'; }} onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--vc-gold)'; }}>
                        {isSubmitting ? (<><Loader2 className="w-4 h-4 animate-spin" />{t('waitlist.submitting')}</>) : t('waitlist.submit')}
                      </button>
                    </div>

                    <div className="flex items-center justify-center gap-2 pt-1">
                      <Lock className="w-3 h-3" style={{ color: '#9ca3af' }} />
                      <p className="text-[11px]" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.privacy')}</p>
                    </div>
                  </form>
                )}
                  </div>{/* end slide animation */}
                </div>{/* end overflow-hidden */}
              </div>

              {/* Success State */}
              <div
                data-testid="waitlist-success-card"
                className="p-5 sm:p-8 lg:p-10 flex flex-col items-center justify-center text-center"
                style={{
                  opacity: isSuccess ? 1 : 0,
                  transform: isSuccess ? 'scale(1)' : 'scale(1.04)',
                  transition: 'opacity 300ms ease 100ms, transform 300ms ease 100ms',
                  position: isSuccess ? 'relative' : 'absolute',
                  inset: isSuccess ? undefined : 0,
                  pointerEvents: isSuccess ? 'auto' : 'none',
                  minHeight: '420px',
                  display: 'flex',
                }}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: 'rgba(214,168,74,0.15)', border: '1px solid rgba(214,168,74,0.45)' }}>
                  <Check className="w-7 h-7" style={{ color: 'var(--vc-gold)' }} />
                </div>
                <h3 data-testid="waitlist-success-message" className="mt-6 text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-ink)' }}>
                  {t('waitlist.success_title')}
                </h3>
                {position && (
                  <div data-testid="waitlist-position" className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(214,168,74,0.1)', border: '1px solid rgba(214,168,74,0.25)' }}>
                    <span className="text-sm font-semibold" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>#{position}</span>
                    <span className="text-xs" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.success_queue')}</span>
                  </div>
                )}
                <p className="mt-3 text-base leading-relaxed max-w-sm" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                  {t('waitlist.success_body')}
                </p>
                <div className="mt-8 w-full max-w-xs">
                  <p className="text-xs font-medium mb-3" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>{t('waitlist.success_share_prompt')}</p>
                  <button data-testid="share-copy-link" onClick={handleCopyLink} className="w-full h-10 rounded-xl text-sm font-medium border flex items-center justify-center gap-2 transition-colors duration-200 active:scale-[0.98]" style={{ borderColor: '#e2e0db', color: copied ? 'var(--vc-gold)' : 'var(--vc-ink-muted)', backgroundColor: copied ? 'rgba(214,168,74,0.06)' : 'transparent', fontFamily: 'Inter, sans-serif' }}>
                    {copied ? (<><Check className="w-3.5 h-3.5" />{t('waitlist.success_copied')}</>) : (<><Copy className="w-3.5 h-3.5" />{t('waitlist.success_copy')}</>)}
                  </button>
                </div>

                {luckyTicket && (
                    <button
                      data-testid="open-lucky-draw-modal"
                      onClick={() => setShowModal(true)}
                      className="mt-6 w-full max-w-xs mx-auto flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-[0.98]"
                      style={{
                        backgroundColor: 'rgba(214,168,74,0.1)',
                        border: '1.5px solid rgba(214,168,74,0.3)',
                        color: 'var(--vc-gold)',
                        fontFamily: 'Inter, sans-serif',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.18)'; e.currentTarget.style.borderColor = 'rgba(214,168,74,0.5)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.1)'; e.currentTarget.style.borderColor = 'rgba(214,168,74,0.3)'; }}
                    >
                      <Gift className="w-4 h-4" />
                      View My Lucky Draw Ticket
                    </button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Benefits Sidebar + Lucky Draw Teaser + Status Lookup */}
          <div className="lg:col-span-5 order-2 space-y-6">
            {/* Benefits */}
            <div className="rounded-[20px] p-6" style={{
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
                {t('lucky_draw.what_you_get')}
              </p>
              <ul className="space-y-3.5">
                {bullets.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-relaxed" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
                    <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(214,168,74,0.12)', border: '1px solid rgba(214,168,74,0.25)' }}>
                      <Check className="w-3 h-3" style={{ color: 'var(--vc-gold)' }} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Check Status */}
            <LuckyDrawStatusCompact />
          </div>
        </div>
      </div>

      {/* Modal rendered at section root to avoid transform containment */}
      {luckyTicket && (
        <LuckyDrawModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          ticket={luckyTicket}
          name={formData.full_name}
          phone={submittedPhone}
        />
      )}
    </section>
  );
};
